import { AlertTriangle, Check, Copy, LoaderCircle, Share2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { BankConfig, FinancingResult, SimulationInput } from "../../shared/contracts";
import { ComparisonTable } from "../components/ComparisonTable";
import { DecisionSummary } from "../components/DecisionSummary";
import { Footer } from "../components/Footer";
import { FoundationChart } from "../components/FoundationChart";
import { GeminiAdvisor } from "../components/GeminiAdvisor";
import { Header } from "../components/Header";
import { InputPanel } from "../components/InputPanel";
import { LeadDialog } from "../components/LeadDialog";
import { ScheduleSection } from "../components/ScheduleSection";
import { TrustSection } from "../components/TrustSection";
import { getBanks } from "../lib/api";
import { flattenResults, rankResults, simulateAll } from "../lib/finance";
import { buildShareUrl, currency, readShareInput } from "../lib/format";

const DEFAULT_INPUT: SimulationInput = {
  propertyValue: 750_000,
  downPayment: 150_000,
  age: 36,
  monthlyIncome: 25_000,
  termMonths: 360
};

interface SavedScenario {
  id: string;
  name: string;
  input: SimulationInput;
}

const STORAGE_KEY = "alicerce.scenarios.v1";

function readSavedScenarios(): SavedScenario[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as SavedScenario[];
    return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
  } catch {
    return [];
  }
}

function validInput(input: SimulationInput): boolean {
  return (
    input.propertyValue >= 50_000 &&
    input.downPayment >= 0 &&
    input.downPayment < input.propertyValue &&
    input.age >= 18 &&
    input.age <= 100 &&
    input.monthlyIncome >= 1_000 &&
    input.termMonths >= 60 &&
    input.termMonths <= 420
  );
}

export function SimulatorPage() {
  const [banks, setBanks] = useState<BankConfig[]>([]);
  const [input, setInput] = useState<SimulationInput>(() => readShareInput() ?? DEFAULT_INPUT);
  const [selectedKey, setSelectedKey] = useState("");
  const [metric, setMetric] = useState<"firstPayment" | "totalPaid">("firstPayment");
  const [scheduleView, setScheduleView] = useState<"annual" | "monthly">("annual");
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>(readSavedScenarios);
  const [leadOpen, setLeadOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    getBanks()
      .then(setBanks)
      .catch((error: unknown) =>
        setLoadError(
          error instanceof Error
            ? error.message
            : "Não foi possível carregar as condições configuradas."
        )
      )
      .finally(() => setLoading(false));
  }, []);

  const results = useMemo(() => {
    if (!validInput(input) || banks.length === 0) return [];
    return rankResults(flattenResults(simulateAll(input, banks)));
  }, [banks, input]);

  const best = results[0];
  const selected =
    results.find((result) => result.key === selectedKey) ??
    results.find((result) => result.eligibility.eligible) ??
    best;
  const eligibleCount = results.filter((result) => result.eligibility.eligible).length;

  function flash(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2_400);
  }

  function saveScenario() {
    const next: SavedScenario = {
      id: crypto.randomUUID(),
      name: `${currency(input.propertyValue)} · ${Math.round(input.termMonths / 12)} anos`,
      input
    };
    const updated = [next, ...savedScenarios].slice(0, 8);
    setSavedScenarios(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    flash("Cenário salvo neste navegador.");
  }

  async function shareScenario() {
    const url = buildShareUrl(input);
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Simulação Alicerce",
          text: "Compare este cenário de financiamento imobiliário.",
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        flash("Link da simulação copiado.");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      await navigator.clipboard.writeText(url);
      flash("Link da simulação copiado.");
    }
  }

  return (
    <>
      <a className="skip-link" href="#simulador">
        Ir para o simulador
      </a>
      <Header />
      <main>
        <section className="workspace" id="simulador">
          <div className="workspace__intro">
            <div>
              <span className="eyebrow">Simulador imobiliário multi-instituições</span>
              <h1>Compare a estrutura antes de assinar a dívida.</h1>
            </div>
            <div className="workspace__intro-aside">
              <p>
                SAC e Price, lado a lado, com custos e critérios expostos. Sem cadastro para
                simular.
              </p>
              <button className="button button--paper" type="button" onClick={shareScenario}>
                <Share2 size={17} aria-hidden="true" />
                Compartilhar cenário
              </button>
            </div>
          </div>

          <div className="blueprint-grid">
            <InputPanel
              input={input}
              onChange={setInput}
              onSave={saveScenario}
              savedScenarios={savedScenarios}
              onLoad={(id) => {
                const scenario = savedScenarios.find((item) => item.id === id);
                if (scenario) {
                  setInput(scenario.input);
                  flash("Cenário restaurado.");
                }
              }}
              onReset={() => {
                setInput(DEFAULT_INPUT);
                window.history.replaceState({}, "", "/");
              }}
            />

            <div className="workspace__result">
              {loading ? (
                <div className="state-card" role="status">
                  <LoaderCircle className="spin" aria-hidden="true" />
                  <h2>Montando a comparação</h2>
                  <p>Carregando premissas e calculando as estruturas.</p>
                </div>
              ) : loadError ? (
                <div className="state-card state-card--error" role="alert">
                  <AlertTriangle aria-hidden="true" />
                  <h2>Não foi possível abrir a régua</h2>
                  <p>{loadError}</p>
                  <button className="button button--primary" onClick={() => window.location.reload()}>
                    Tentar novamente
                  </button>
                </div>
              ) : !validInput(input) || !best ? (
                <div className="state-card state-card--error" role="alert">
                  <AlertTriangle aria-hidden="true" />
                  <h2>Revise a base da simulação</h2>
                  <p>
                    A entrada precisa ser menor que o imóvel e os demais valores devem permanecer
                    dentro dos limites indicados.
                  </p>
                </div>
              ) : (
                <DecisionSummary
                  best={best}
                  input={input}
                  eligibleCount={eligibleCount}
                  totalCount={results.length}
                  onLead={() => setLeadOpen(true)}
                />
              )}
            </div>
          </div>

          <div className="method-strip" aria-label="Premissas desta ferramenta">
            <span>
              <Check size={15} aria-hidden="true" /> cálculo local e instantâneo
            </span>
            <span>
              <Check size={15} aria-hidden="true" /> sem consulta ao CPF
            </span>
            <span>
              <Check size={15} aria-hidden="true" /> taxas ilustrativas editáveis
            </span>
            <a href="/metodologia">ver metodologia</a>
          </div>
        </section>

        {best && selected && (
          <div className="results-flow">
            <GeminiAdvisor input={input} selected={selected} results={results} />
            <FoundationChart
              results={results}
              selectedKey={selected.key}
              metric={metric}
              onMetricChange={setMetric}
              onSelect={setSelectedKey}
            />
            <ComparisonTable
              results={results}
              selectedKey={selected.key}
              onSelect={setSelectedKey}
            />
            <ScheduleSection
              result={selected}
              view={scheduleView}
              onViewChange={setScheduleView}
            />
          </div>
        )}

        <TrustSection />
      </main>
      <Footer />

      {selected && (
        <LeadDialog
          open={leadOpen}
          onClose={() => setLeadOpen(false)}
          input={input}
          result={selected as FinancingResult}
        />
      )}

      {notice && (
        <div className="toast" role="status">
          <Copy size={16} aria-hidden="true" />
          {notice}
        </div>
      )}
    </>
  );
}
